import { LifeBuoyIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import type { SupportRequestDto } from '@/lib/types';
import { STATUS_LABEL } from './SupportRequestPage';
import { useMyRequests } from './queries';

/** The signed-in person's own help requests, each opening its conversation. */
export function MyRequestsPage() {
  const list = useMyRequests();
  const columns: Array<Column<SupportRequestDto>> = [
    { id: 'received', header: 'Sent', sortValue: (r) => r.createdAt, cell: (r) => <span className="text-muted-foreground whitespace-nowrap" title={fmtDateTime(r.createdAt)}>{fmtRelative(r.createdAt)}</span> },
    { id: 'reference', header: 'Reference', sortValue: (r) => r.reference, searchValue: (r) => r.reference, cell: (r) => <span className="font-mono text-xs">{r.reference}</span> },
    { id: 'subject', header: 'Subject', sortValue: (r) => r.subject, searchValue: (r) => `${r.subject} ${r.categoryLabel}`, cell: (r) => <span className="font-medium">{r.subject}</span> },
    { id: 'topic', header: 'Topic', sortValue: (r) => r.categoryLabel, cell: (r) => <span className="text-muted-foreground">{r.categoryLabel}</span> },
    { id: 'replies', header: 'Replies', align: 'right', sortValue: (r) => r.messageCount ?? 0, cell: (r) => <span className="tabular-nums">{r.messageCount ?? 0}</span> },
    { id: 'status', header: 'Status', sortValue: (r) => r.status, cell: (r) => <Badge variant={r.status === 'new' ? 'default' : 'secondary'}>{STATUS_LABEL[r.status]}</Badge> },
  ];
  return (
    <div className="space-y-8">
      <PageHeader
        title="My help requests"
        description="Everything you have sent to the team, with their replies."
        actions={
          <Button asChild>
            <Link to="/help">
              <PlusIcon /> New request
            </Link>
          </Button>
        }
      />
      <DataTable
        data={list.data?.items}
        loading={list.isPending}
        error={list.isError ? list.error : undefined}
        onRetry={() => void list.refetch()}
        columns={columns}
        rowId={(r) => r.id}
        rowHref={(r) => `/support/${r.id}`}
        defaultSort={{ id: 'received', dir: 'desc' }}
        searchPlaceholder="Search your requests…"
        emptyState={
          <EmptyState
            icon={LifeBuoyIcon}
            title="No requests yet"
            description="Stuck on something? Send a request and the team will reply here and by email."
            action={
              <Button asChild>
                <Link to="/help">Ask for help</Link>
              </Button>
            }
          />
        }
      />
    </div>
  );
}
